import { useEffect, useRef, useState } from "react";
import { Send, Paperclip, Languages, Loader2, ImageIcon, X, ArrowRight } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const MAX_IMAGES = 6;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

async function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

/** Customer-side support chat with Meyar admin. Text + up to 6 images + per-message translate. */
export default function Support() {
    const navigate = useNavigate();
    const { company } = useAuth();
    const [params] = useSearchParams();
    const fromLedgers = params.get("from") === "select-ledgers";
    const draftKey = `meyar_chat_draft_${company?.id || "anon"}`;
    const [messages, setMessages] = useState([]);
    const [text, setTextRaw] = useState(() => {
        try { return window.localStorage.getItem(draftKey) || ""; } catch { return ""; }
    });
    const setText = (v) => {
        setTextRaw(v);
        try { v ? window.localStorage.setItem(draftKey, v) : window.localStorage.removeItem(draftKey); } catch { /* ignore */ }
    };
    const [images, setImages] = useState([]);  // data URLs
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [translating, setTranslating] = useState(null);  // message id while translating
    const [showOriginal, setShowOriginal] = useState({});  // {msgId: bool} — true = show original even if translated exists
    const scrollRef = useRef(null);
    const fileRef = useRef(null);

    const load = async () => {
        try {
            const r = await api.get("/chat/messages");
            setMessages(r.data.messages || []);
            await api.post("/chat/mark-read");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(() => { if (document.visibilityState === "visible") load(); }, 10000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages.length]);

    const onFiles = async (files) => {
        const picked = Array.from(files || []);
        const room = MAX_IMAGES - images.length;
        if (room <= 0) {
            toast.error(`يمكنك إرفاق ${MAX_IMAGES} صور كحد أقصى`);
            return;
        }
        const slice = picked.slice(0, room);
        const next = [...images];
        for (const f of slice) {
            if (f.size > MAX_IMAGE_BYTES) { toast.error(`${f.name}: الحد الأقصى 2MB`); continue; }
            if (!f.type.startsWith("image/")) continue;
            try { next.push(await fileToDataUrl(f)); } catch {}
        }
        setImages(next);
    };

    const send = async () => {
        const txt = text.trim();
        if (!txt && images.length === 0) return;
        setSending(true);
        try {
            await api.post("/chat/messages", { text: txt, images });
            setText(""); setImages([]);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setSending(false); }
    };

    const translate = async (msg) => {
        // Guess target: if message likely Arabic, translate to English; else to Arabic.
        const hasAr = /[\u0600-\u06FF]/.test(msg.text || "");
        const target = hasAr ? "en" : "ar";
        setTranslating(msg.id);
        try {
            const r = await api.post(`/chat/messages/${msg.id}/translate`, { target_lang: target });
            setMessages((cur) => cur.map((m) => (m.id === msg.id ? { ...m, translated_text: r.data.translated_text, translated_lang: r.data.translated_lang } : m)));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setTranslating(null); }
    };

    return (
        <AppShell>
            <div className="mx-auto flex max-w-3xl flex-col gap-3" dir="rtl">
                <div className="flex items-center gap-2">
                    {fromLedgers ? (
                        <Button variant="outline" size="sm" onClick={() => navigate("/select-ledgers")} className="gap-1 shrink-0 border-amber-300 text-amber-900" data-testid="chat-back-to-ledgers-btn">
                            <ArrowRight className="h-4 w-4" />
                            رجوع لاختيار الدفاتر
                        </Button>
                    ) : (
                        <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="gap-1 shrink-0" data-testid="chat-back-home-btn">
                            <ArrowRight className="h-4 w-4" />
                            الرئيسية
                        </Button>
                    )}
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-neutral-900">المحادثات</h1>
                        <p className="mt-1 text-sm text-neutral-500">
                            {fromLedgers
                                ? "طلب دفتر: اشرح لإدارة معيار الدفتر الذي تريده والتعديلات المطلوبة، ويمكنك إرفاق صور."
                                : "تواصل مع إدارة معيار لطلبات التصميم أو الدعم أو الاستفسارات."}
                        </p>
                    </div>
                </div>

                <div ref={scrollRef} className="flex max-h-[65vh] min-h-[50vh] flex-col gap-2 overflow-y-auto rounded-2xl bg-neutral-50 p-4 ring-1 ring-neutral-200" data-testid="chat-scroll">
                    {loading ? (
                        <div className="m-auto text-sm text-neutral-500">...جاري التحميل</div>
                    ) : messages.length === 0 ? (
                        <div className="m-auto max-w-xs text-center text-sm text-neutral-500">
                            ابدأ المحادثة. يمكنك إرسال نص وصور.
                        </div>
                    ) : (
                        messages.map((m) => {
                            const mine = m.sender === "user";
                            const show = showOriginal[m.id];
                            return (
                                <div key={m.id} className={`flex ${mine ? "justify-start" : "justify-end"}`} data-testid={`chat-msg-${m.id}`}>
                                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 shadow-sm ${mine ? "bg-amber-700 text-white" : "bg-white text-neutral-900 ring-1 ring-neutral-200"}`}>
                                        {m.text ? (
                                            <div className="whitespace-pre-wrap break-words text-sm">
                                                {m.translated_text && !show ? m.translated_text : m.text}
                                            </div>
                                        ) : null}
                                        {m.images && m.images.length > 0 ? (
                                            <div className="mt-2 grid grid-cols-2 gap-1.5">
                                                {m.images.map((src, i) => (
                                                    <a key={i} href={src} target="_blank" rel="noreferrer">
                                                        <img src={src} alt="" className="h-24 w-full rounded object-cover ring-1 ring-black/5" />
                                                    </a>
                                                ))}
                                            </div>
                                        ) : null}
                                        <div className={`mt-1 flex items-center justify-between gap-2 text-[10px] ${mine ? "text-amber-100" : "text-neutral-500"}`}>
                                            <span>{new Date(m.created_at).toLocaleString("ar-SA")}</span>
                                            {m.text ? (
                                                m.translated_text ? (
                                                    <button
                                                        onClick={() => setShowOriginal((s) => ({ ...s, [m.id]: !s[m.id] }))}
                                                        className="font-bold hover:underline"
                                                        data-testid={`chat-toggle-${m.id}`}
                                                    >
                                                        {show ? "عرض الترجمة" : "عرض الأصل"}
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => translate(m)}
                                                        disabled={translating === m.id}
                                                        className="inline-flex items-center gap-1 font-bold hover:underline"
                                                        data-testid={`chat-translate-${m.id}`}
                                                    >
                                                        {translating === m.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Languages className="h-3 w-3" />}
                                                        ترجمة
                                                    </button>
                                                )
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {images.length > 0 ? (
                    <div className="flex flex-wrap gap-2 rounded-lg bg-white p-2 ring-1 ring-neutral-200">
                        {images.map((src, i) => (
                            <div key={i} className="relative">
                                <img src={src} alt="" className="h-14 w-14 rounded object-cover ring-1 ring-neutral-200" />
                                <button
                                    onClick={() => setImages((cur) => cur.filter((_, j) => j !== i))}
                                    className="absolute -end-1 -top-1 rounded-full bg-red-600 p-0.5 text-white"
                                    data-testid={`chat-remove-img-${i}`}
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                ) : null}

                <div className="flex items-end gap-2 rounded-2xl bg-white p-2 ring-1 ring-neutral-200">
                    <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} data-testid="chat-file-input" />
                    <Button variant="ghost" size="icon" onClick={() => fileRef.current?.click()} data-testid="chat-attach-btn">
                        <Paperclip className="h-5 w-5 text-neutral-600" />
                    </Button>
                    <Textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="اكتب رسالتك..."
                        className="min-h-[42px] flex-1 resize-none border-0 shadow-none focus-visible:ring-0"
                        rows={1}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        data-testid="chat-input"
                    />
                    <Button
                        onClick={send}
                        disabled={sending || (!text.trim() && images.length === 0)}
                        className="h-10 gap-1 bg-amber-700 text-white hover:bg-amber-800"
                        data-testid="chat-send-btn"
                    >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        إرسال
                    </Button>
                </div>
            </div>
        </AppShell>
    );
}
